import {action, computed, observable, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import React, {Component} from 'react';

//lodash
import invoke from 'lodash/invoke';
import omit from 'lodash/omit';

import GradientAnimation from 'components/GradientAnimation';

//helpers
import {isEdge, isIE, isObjectFitSupported} from 'utils/browser-utils';

//styled components
import {LegacyImage, Picture, StyledIcon, StyledImage, StyledSpinner} from './styles';
import Overlay from '../Overlay';

//constants
import {PICTURE_STATUS} from './constants';

@observer
class PictureComponent extends Component {
  static defaultProps = {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    emptyIcon: <StyledIcon name="image" />,
    objectFit: 'cover',
    objectPositionX: 0.5,
    objectPositionY: 0.5,
    shouldShowSpinnerWhileLoading: false,
    shouldShowOverlayShapes: false,
    overlayShapes: []
  };

  static propTypes = {
    backgroundColor: PropTypes.string,
    emptyIcon: PropTypes.node,
    objectFit: PropTypes.oneOf(['contain', 'cover']),
    objectPositionX: PropTypes.number,
    objectPositionY: PropTypes.number,
    onLoad: PropTypes.func,
    restartAnimation: PropTypes.bool,
    shouldShowSpinnerWhileLoading: PropTypes.bool,
    shouldShowOverlayShapes: PropTypes.bool,
    overlayShapes: PropTypes.array,
    sizes: PropTypes.string,
    src: PropTypes.string,
    srcSet: PropTypes.string
  };

  @observable legacySrc = null;
  @observable naturalAspectRatio = 0;
  @observable status = PICTURE_STATUS.EMPTY;
  imageElement = null;
  useLegacy = !isObjectFitSupported() || isEdge() || isIE();

  constructor(props) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    const {src, srcSet} = this.props;

    if (src || srcSet) {
      this.markAsLoading();
    }
  }

  componentDidUpdate(prevProps) {
    const {restartAnimation, src, srcSet} = this.props;

    if (prevProps.restartAnimation !== restartAnimation) {
      this.restartAnimation();
    }

    if (prevProps.src !== src || prevProps.srcSet !== srcSet) {
      if (src || srcSet) {
        this.markAsLoading();
      } else {
        this.markAsEmpty();
      }
    }
  }

  @computed
  get isEmpty() {
    return this.status === PICTURE_STATUS.EMPTY;
  }

  @computed
  get isFailed() {
    return this.status === PICTURE_STATUS.FAILED;
  }

  @computed
  get isLoading() {
    return this.status === PICTURE_STATUS.LOADING;
  }

  @computed
  get isReady() {
    return this.status === PICTURE_STATUS.READY;
  }

  setImageElement = value => {
    this.imageElement = value;
  };

  @action
  setLegacySrc = value => {
    this.legacySrc = value;
  };

  @action
  setNaturalAspectRatio = naturalAspectRatio => {
    this.naturalAspectRatio = naturalAspectRatio;
  };

  @action
  setStatus = value => {
    this.status = value;
  };

  handleImageError = () => {
    Sentry.captureException(new Error('Image failed to load'), {
      extra: {
        imageSrc: this.props.src
      }
    });
    this.markAsFailed();
  };

  handleImageLoad = event => {
    this.setNaturalAspectRatio(this.imageElement.naturalWidth / this.imageElement.naturalHeight);
    this.markAsReady();
    invoke(this.props, 'onLoad', event);
  };

  markAsEmpty = () => {
    this.setLegacySrc(null);
    this.setStatus(PICTURE_STATUS.EMPTY);
  };

  markAsFailed = () => {
    this.setLegacySrc(null);
    this.setStatus(PICTURE_STATUS.FAILED);
  };

  markAsLoading = () => {
    this.setLegacySrc(null);
    this.setStatus(PICTURE_STATUS.LOADING);
  };

  markAsReady = () => {
    const currentSrc = this.imageElement.currentSrc || this.imageElement.src;

    this.setLegacySrc(currentSrc);
    this.setStatus(PICTURE_STATUS.READY);
  };

  restartAnimation = () => {
    if (!this.imageElement || !this.isReady) {
      return;
    }

    if (this.useLegacy) {
      // NB. This is mainly for Internet Explorer. While appending
      // the URL with a cachebuster is enough for it to restart
      // the animation, it would still be showing the current animation
      // until the "new" file loads. To avoid that, we are removing
      // the current background and assigning a new one only in the
      // next animation frame.

      const currentSrc = this.imageElement.currentSrc || this.imageElement.src;
      const separator = currentSrc.includes('?') ? '&' : '?';
      const cachebuster = `restart=${Date.now().toString(16)}`;

      this.setLegacySrc(null);

      window.requestAnimationFrame(() => {
        this.setLegacySrc(`${currentSrc}${separator}${cachebuster}`);
      });
    } else {
      // NB. Normally, you would only require to reset src / srcset
      // to the value they've already had. However, this doesn't work
      // in Firefox -- it requires the attributes to be removed first
      // and then restored in the next animation frame.

      const {src, srcset} = this.imageElement;
      this.imageElement.removeAttribute('src');
      this.imageElement.removeAttribute('srcset');

      window.requestAnimationFrame(() => {
        this.imageElement.src = src;
        this.imageElement.srcset = srcset;
      });
    }
  };

  render() {
    const {
      backgroundColor,
      emptyIcon,
      metadata,
      objectFit,
      objectPositionX,
      objectPositionY,
      shouldShowSpinnerWhileLoading,
      shouldShowAnimationWhileLoading,
      shouldShowOverlayShapes,
      overlayShapes,
      sizes,
      src,
      srcSet
    } = this.props;

    const baseImageProps = {
      isTransparent: !this.isReady,
      objectFit,
      objectPositionX,
      objectPositionY
    };

    const forwardedProps = omit(this.props, Object.keys(PictureComponent.propTypes));

    return (
      <Picture backgroundColor={backgroundColor} {...forwardedProps}>
        {this.isLoading && shouldShowAnimationWhileLoading && <GradientAnimation />}
        {!this.isEmpty && (
          <StyledImage
            ref={this.setImageElement}
            isHidden={this.useLegacy}
            onError={this.handleImageError}
            onLoad={this.handleImageLoad}
            sizes={sizes}
            src={src}
            srcSet={srcSet}
            {...baseImageProps}
          />
        )}
        {this.useLegacy && <LegacyImage src={this.legacySrc} {...baseImageProps} />}
        {this.isEmpty && emptyIcon}
        {this.isFailed && <StyledIcon name="image-broken" />}
        {this.isLoading && shouldShowSpinnerWhileLoading && <StyledSpinner />}
        {shouldShowOverlayShapes && !this.isLoading && (
          <Overlay
            imageAspectRatio={this.naturalAspectRatio}
            imageFit={objectFit}
            imageMetadata={metadata}
            imageObjectPositionX={objectPositionX}
            imageObjectPositionY={objectPositionY}
            overlayShapes={overlayShapes}
            shouldShowOverlayShapes={shouldShowOverlayShapes}
          />
        )}
      </Picture>
    );
  }
}

export default PictureComponent;
